import React, { useEffect, useMemo, useState, CSSProperties } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import TenantSignupLayout from '../TenantSignupLayout';
import { API } from 'aws-amplify';
import * as queries from '../../graphql/queries';
import { FACILITIES } from '../../utils/facilities';

type LocationState = {
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zip: string;
  specialInstructions: string;
  parking: string;
  building: string;
  referralCode?: string;
  showFacility: 'Richmond' | 'Tacoma' | string;
};

type MovingCosts = {
  flatFee?: number | null;
  hourlyFee?: number | null;
};

const baseFont: CSSProperties = {
  fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  color: '#222',
  fontSize: 14,
};

const labelStyle: CSSProperties = {
  ...baseFont,
  fontSize: 13,
  fontWeight: 500,
  marginBottom: 4,
};

const pillLabel = (minStay: string) => {
  switch (minStay) {
    case 'FOUR_MONTH':
      return '4-month minimum';
    case 'EIGHT_MONTH':
      return '8-month minimum';
    case 'NONE':
      return 'No minimum';
    default:
      return minStay;
  }
};

const planTypeLabel = (type: string) => {
  switch (type) {
    case 'SELF':
      return 'Do it myself';
    case 'SOME_HELP':
      return 'I need help';
    case 'UNLIMITED_HELP':
      return 'Unlimited help';
    default:
      return type;
  }
};

const SelectFacilityPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation<LocationState | undefined>();
  const addressState = location.state;

  const [dataSource, setDataSource] = useState<any[]>([]);
  const [units, setUnits] = useState<any[]>([]);
  const [movingCosts, setMovingCosts] = useState<MovingCosts>({});
  const [minStayOfPlan, setMinStayOfPlan] = useState<string>('NONE');
  const [typeOfPlan, setTypeOfPlan] = useState<string>('SELF');
  const [loading, setLoading] = useState<boolean>(false);

  // If user hits this URL directly without the address step
  if (!addressState) {
    return (
      <TenantSignupLayout
        title="Almost there!"
        subtitle="We need your address first so we can match you to the right facility."
        step={2}
        totalSteps={7}
      >
        <div style={{ ...baseFont }}>
          <p style={{ marginBottom: 16 }}>
            It looks like you opened this page directly. Please start from the first step of signup.
          </p>
          <button
            type="button"
            onClick={() => history.push('/signup')}
            style={{
              ...baseFont,
              padding: '10px 16px',
              borderRadius: 6,
              border: 'none',
              backgroundColor: '#7B1FA2',
              color: '#fff',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Go to signup
          </button>
        </div>
      </TenantSignupLayout>
    );
  }

  // Map facility based on showFacility (same IDs/addresses as RN)
  const { facilityID, facilityAddress, facilityName, facilityWebsite } = useMemo(() => {
    const key = addressState.showFacility === 'Richmond' ? 'richmond' : 'tacoma';
    const f = FACILITIES[key];
    return {
      facilityID: f.id,
      facilityAddress: f.address,
      facilityName: 'CSI Mini Storage',
      facilityWebsite: 'csiministorage.com',
    };
  }, [addressState.showFacility]);

  // Fetch plans + movingCosts
  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        // Plans
        const facilityPlans: any = await API.graphql({
          query: queries.plansByFacility,
          variables: { facilityID },
          authMode: 'AWS_IAM',
        });

        let planData = facilityPlans?.data?.plansByFacility?.items ?? [];

        planData.sort((a: any, b: any) => {
          const priceA = parseInt(String(a.price || '').replace('$', '').replace('/month', ''), 10);
          const priceB = parseInt(String(b.price || '').replace('$', '').replace('/month', ''), 10);
          if (priceA < priceB) return -1;
          if (priceA > priceB) return 1;
          return 0;
        });

        setDataSource(planData);

        const initialUnits = planData.filter(
          (d: any) => d.minStay === 'NONE' && d.type === 'SELF'
        );
        setUnits(initialUnits);
        setMinStayOfPlan('NONE');
        setTypeOfPlan('SELF');

        // Moving costs
        const movingRes: any = await API.graphql({
          query: `
            query GetFacility($id: ID!) {
              getFacility(id: $id) {
                movingCosts {
                  flatFee
                  hourlyFee
                }
              }
            }
          `,
          variables: { id: facilityID },
          authMode: 'AWS_IAM',
        });

        const costData = movingRes?.data?.getFacility?.movingCosts || {};
        setMovingCosts(costData);
      } catch (err) {
        console.log('error fetching facility data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [facilityID]);

  // Filter options
  const minStayOptions = useMemo(() => {
    const vals = Array.from(new Set(dataSource.map(p => p.minStay))).filter(Boolean);
    // Ensure NONE is first
    const hasNone = vals.includes('NONE');
    const rest = vals.filter(v => v !== 'NONE');
    return hasNone ? ['NONE', ...rest] : ['NONE', ...rest];
  }, [dataSource]);

  const typeOptions = useMemo(() => {
    const vals = Array.from(new Set(dataSource.map(p => p.type))).filter(Boolean);
    const hasSelf = vals.includes('SELF');
    const rest = vals.filter(v => v !== 'SELF');
    return hasSelf ? ['SELF', ...rest] : ['SELF', ...rest];
  }, [dataSource]);

  const onSort = (minStay: string, type: string) => {
    setMinStayOfPlan(minStay);
    setTypeOfPlan(type);

    const filtered = dataSource.filter(
      (d: any) => d.minStay === minStay && d.type === type
    );
    setUnits(filtered);
  };

  const handleSelectPlan = (plan: any) => {
    const unitSize =
      plan.unitSize || plan.size || plan.name || plan.title || 'Unit';

    const addressData = {
      addressLine1: addressState.addressLine1,
      addressLine2: addressState.addressLine2,
      city: addressState.city,
      state: addressState.state,
      zip: addressState.zip,
      specialInstructions: addressState.specialInstructions,
      parking: addressState.parking,
      building: addressState.building,
      size: unitSize,
      minStay: plan.minStay,
      typeOfPlan: plan.type,
      price: plan.price,
      facilityID,
      referralCode: addressState.referralCode,
    };

    history.push('/signup/account-info', addressData);
  };

  return (
    <TenantSignupLayout
      title="Choose your facility & plan"
      subtitle="We’ve matched you with a partner facility based on your address."
      step={2}
      totalSteps={7}
    >
      <div style={{ ...baseFont }}>
        {/* Facility summary */}
        <div
          style={{
            borderRadius: 10,
            backgroundColor: '#F7F4FB',
            padding: 16,
            marginBottom: 20,
          }}
        >
          <div style={{ marginBottom: 4, fontSize: 16, fontWeight: 600, color: '#261136' }}>
            {facilityName}
          </div>
          <div style={{ marginBottom: 4, fontSize: 14, color: '#444' }}>
            {facilityAddress}
          </div>
          <div style={{ marginBottom: 4, fontSize: 13 }}>
            <a
              href={`https://${facilityWebsite}`}
              target="_blank"
              rel="noreferrer"
              style={{ color: '#7B1FA2', textDecoration: 'none' }}
            >
              {facilityWebsite}
            </a>
          </div>

        </div>

        {/* Filters */}
        <div
          style={{
            display: 'flex',
            gap: 12,
            marginBottom: 16,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ flex: 1, minWidth: 140 }}>
            <label style={labelStyle}>Minimum stay</label>
            <select
              value={minStayOfPlan}
              onChange={e => onSort(e.target.value, typeOfPlan)}
              style={selectStyle}
            >
              {minStayOptions.map(opt => (
                <option key={opt} value={opt}>
                  {pillLabel(opt)}
                </option>
              ))}
            </select>
          </div>
          <div style={{ flex: 1, minWidth: 140 }}>
            <label style={labelStyle}>Plan type</label>
            <select
              value={typeOfPlan}
              onChange={e => onSort(minStayOfPlan, e.target.value)}
              style={selectStyle}
            >
              {typeOptions.map(opt => (
                <option key={opt} value={opt}>
                  {planTypeLabel(opt)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Plans list */}
        {loading ? (
          <div style={{ fontSize: 14, color: '#555' }}>Loading plans…</div>
        ) : units.length === 0 ? (
          <div style={{ fontSize: 14, color: '#555' }}>
            No plans available with that combination. Try a different minimum stay or plan type.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {units.map(plan => {
              const unitSize =
                plan.unitSize || plan.size || plan.name || plan.title || 'Unit';

              return (
                <div
                  key={plan.id}
                  style={{
                    borderRadius: 10,
                    border: '1px solid #E0D8F4',
                    padding: 14,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 12,
                    flexWrap: 'wrap',
                  }}
                >
                  <div>
                    <div
                      style={{
                        ...baseFont,
                        fontSize: 16,
                        fontWeight: 600,
                        marginBottom: 4,
                      }}
                    >
                      {unitSize}
                    </div>
                    <div style={{ ...baseFont, fontSize: 14, color: '#555' }}>
                      <span style={{ fontWeight: 600 }}>{plan.price}</span>
                      {' · '}
                      {pillLabel(plan.minStay)}
                      {' · '}
                      {planTypeLabel(plan.type)}
                    </div>
                    {plan.description && (
                      <div style={{ ...baseFont, fontSize: 13, color: '#777', marginTop: 4 }}>
                        {plan.description}
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => handleSelectPlan(plan)}
                    style={{
                      ...baseFont,
                      padding: '8px 16px',
                      borderRadius: 6,
                      border: 'none',
                      backgroundColor: '#7B1FA2',
                      color: '#fff',
                      fontWeight: 600,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Select
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </TenantSignupLayout>
  );
};

const selectStyle: CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 6,
  border: '1px solid #CCCCCC',
  fontSize: 14,
  fontFamily: baseFont.fontFamily,
  boxSizing: 'border-box',
  backgroundColor: '#fff',
};

export default SelectFacilityPage;
